import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Lightbox } from '../components/Lightbox';
import { PhotoGrid } from '../components/PhotoGrid';
import { BackIcon, PhotosIcon, UploadIcon } from '../components/Icons';
import { api, type Photo } from '../lib/api';
import { readCachedAlbumPhotos, writeCachedAlbumPhotos } from '../lib/albumPhotoCache';

/** One album. Uploading here files straight into that album's folder. */
export function AlbumPage() {
  const { user } = useAuth();
  const { name = '' } = useParams();
  const navigate = useNavigate();

  const albumName = decodeURIComponent(name);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [cacheStatus, setCacheStatus] = useState<'idle' | 'refreshing' | 'saved'>('idle');

  const fileInput = useRef<HTMLInputElement>(null);
  const photosRef = useRef<Photo[]>([]);
  const hasCachedListingRef = useRef(false);
  const loadRequestVersion = useRef(0);

  const load = useCallback(async () => {
    const requestVersion = ++loadRequestVersion.current;
    const hasSavedListing = hasCachedListingRef.current || photosRef.current.length > 0;
    setLoading(!hasSavedListing);
    if (hasSavedListing) setCacheStatus('refreshing');
    try {
      const result = await api.albumPhotos(albumName);
      if (requestVersion !== loadRequestVersion.current) return;
      const next = result.photos.map((photo) => ({ ...photo, album: albumName }));
      photosRef.current = next;
      hasCachedListingRef.current = true;
      setPhotos(next);
      setError(null);
      setCacheStatus('idle');
      if (user) void writeCachedAlbumPhotos(user.id, albumName, next);
    } catch (caught) {
      if (requestVersion !== loadRequestVersion.current) return;
      if (hasCachedListingRef.current) {
        setError(null);
        setCacheStatus('saved');
      } else {
        setError(caught instanceof Error ? caught.message : 'Could not load this album.');
      }
    } finally {
      if (requestVersion === loadRequestVersion.current) setLoading(false);
    }
  }, [albumName, user]);

  useEffect(() => {
    let current = true;
    const hydrationVersion = ++loadRequestVersion.current;
    photosRef.current = [];
    hasCachedListingRef.current = false;
    setPhotos([]);
    setLoading(true);
    setError(null);
    setCacheStatus('idle');
    void (async () => {
      const cached = user ? await readCachedAlbumPhotos(user.id, albumName) : null;
      if (!current || hydrationVersion !== loadRequestVersion.current) return;
      if (cached) {
        photosRef.current = cached;
        hasCachedListingRef.current = true;
        setPhotos(cached);
        setLoading(false);
        setCacheStatus('refreshing');
      }
      if (current) await load();
    })();
    return () => {
      current = false;
    };
  }, [albumName, load, user]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadProgress(0);
    try {
      await api.uploadToAlbum(Array.from(files), albumName, setUploadProgress);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Upload failed.');
    } finally {
      setUploadProgress(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <>
      <header className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <button
            type="button"
            onClick={() => navigate('/albums')}
            aria-label="Back to albums"
            style={{ color: 'var(--text)', display: 'grid', placeItems: 'center' }}
          >
            <BackIcon className="" />
          </button>
          <h1
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '1.15rem',
            }}
          >
            {albumName}
          </h1>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => fileInput.current?.click()}
          disabled={uploadProgress !== null}
        >
          <UploadIcon className="" />
          Add
        </button>
      </header>

      <div className="page">
        {cacheStatus === 'refreshing' && <div className="album-refresh-progress" role="status" aria-label="Refreshing album"><span /></div>}
        <input
          ref={fileInput}
          type="file"
          accept="image/*,video/*"
          multiple
          className="visually-hidden"
          onChange={(event) => void handleUpload(event.target.files)}
        />

        {uploadProgress !== null && (
          <div className="banner">
            <h3>Preparing & uploading…</h3>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${uploadProgress * 100}%` }} />
            </div>
          </div>
        )}

        {error && (
          <div className="banner error">
            <p>{error}</p>
          </div>
        )}

        {cacheStatus === 'saved' && (
          <div className="feed-cache-notice album-cache-notice" role="status">
            <div><strong>Showing saved photos</strong><span>This album could not refresh from the family cloud.</span></div>
            <button type="button" onClick={() => void load()}>Try again</button>
          </div>
        )}

        {loading ? (
          <div className="center-fill">
            <div className="spinner" role="status" aria-label="Loading album" />
          </div>
        ) : photos.length === 0 ? (
          <div className="empty-state">
            <PhotosIcon />
            <h2>This album is empty</h2>
            <p>Tap Add to put the first photos in.</p>
          </div>
        ) : (
          <PhotoGrid photos={photos} onSelect={(_photo, index) => setSelected(index)} />
        )}
      </div>

      {selected !== null && (
        <Lightbox
          photos={photos}
          index={selected}
          initialPreviewSize={400}
          onClose={() => setSelected(null)}
          onIndexChange={setSelected}
        />
      )}
    </>
  );
}
